import { Icon } from '@assembly-js/design-system';
import { MenuProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  FiEdit as EditIcon,
  FiExternalLink as OpenPortalIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';

import { COACHMARK_HIGHLIGHT_CLASS } from 'src/components/Coachmark';
import { DetailsIcon, TrashIcon } from 'src/components/icons';
import { useIsStudioUser } from 'src/hooks/useIsStudioUser';
// eslint-disable-next-line no-restricted-imports
import { Action, BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';

import { ClientActionsRendererProps } from './types';

// Render the tour's menu as a non-modal popover: let clicks fall through its root
// to the page (so the backdrop no longer catches click-away and the menu stays
// open) while keeping the paper interactive, and drop the modal's scroll lock,
// focus trap, and Escape-to-close so the rest of the page stays fully usable.
const COACHMARK_MENU_PROPS: Partial<MenuProps> = {
  disableScrollLock: true,
  disableEnforceFocus: true,
  disableEscapeKeyDown: true,
  slotProps: {
    root: { style: { pointerEvents: 'none' } },
    paper: { style: { pointerEvents: 'auto' } },
  },
};

export const ClientActionRenderer = ({
  data,
  handleClickRemove,
  handleClickEdit,
  handleClickInvite,
  handleClickCopyAccessLink,
  handleClickEmailAccessLink,
  handleClickViewDetails,
  handleClickTestAsClient,
  onActionMenuClick,
  isCoachmarkActive = false,
  onOpenPortalCoachmark,
}: ClientActionsRendererProps) => {
  const { t } = useTranslation();

  const isStudioUser = useIsStudioUser();

  const actions: Action[] = [
    {
      name: 'Edit info',
      icon: <EditIcon />,
      onClick: () => handleClickEdit(data),
    },
  ];
  if (data.status !== 'Active' && handleClickInvite) {
    actions.push({
      name: `${
        data.status === 'Inactive' ? 'Send invite' : 'Invite client'
      }...`,
      onClick: () => handleClickInvite(data),
      icon: <NavigationIcon />,
    });
  }

  if (handleClickEmailAccessLink) {
    actions.push({
      name: `Email magic link`,
      onClick: () => handleClickEmailAccessLink(data),
      icon: <Icon icon="Invite" className="h-4 w-4 text-gray-small" />,
    });
  }
  if (handleClickCopyAccessLink) {
    actions.push({
      name: `Copy magic link`,
      onClick: () => handleClickCopyAccessLink(data),
      icon: <Icon icon="Copy" className="h-4 w-4 text-gray-small" />,
    });
  }

  if (handleClickViewDetails) {
    actions.unshift({
      name: 'View details',
      icon: <DetailsIcon />,
      onClick: () => handleClickViewDetails(data),
    });
  }
  actions.push({
    name: t(($) => $.actions.delete_client),
    onClick: () => handleClickRemove(data),
    icon: <TrashIcon />,
    isDelete: true,
  });

  // Coachmark presentation, only while the tour's menu step is active and only on
  // the first row (the one the tour opens): a lit "Open portal" item on top, every
  // real action dimmed. Clicking "Open portal" completes the tour.
  const isCoachmarkMenu = isStudioUser;
  const menuActions: Action[] = isCoachmarkMenu
    ? [
        {
          name: t(($) => $.crm.coachmark.menu.open_portal),
          icon: <OpenPortalIcon />,
          onClick: () => {
            handleClickTestAsClient?.(data);
            onOpenPortalCoachmark?.();
          },
          classes: isCoachmarkActive ? [COACHMARK_HIGHLIGHT_CLASS] : [],
        },
        ...actions.map((action) => ({
          ...action,
          isDisabled: isCoachmarkActive,
        })),
      ]
    : actions;

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={menuActions}
      menuPropsOverride={
        isCoachmarkMenu && isCoachmarkActive ? COACHMARK_MENU_PROPS : undefined
      }
    />
  );
};
